import Parse from 'parse';
import { InjectionToken, Provider } from '@angular/core';
import { PipelineRepository } from '@telespot/domain';
import { ParsePipelineRepository } from '@telespot/infrastructure/parse';
import { ParseLabelNamesRepository } from '@protocols/infra/parse';
import { type ILabelNamesPort, RetrievePipelineDetailQuery } from '@protocols/use-cases';

import { PaginatedStateLoadingMode } from '@shared/ui';

import { PipelineEditorEffects, RETRIEVE_PIPELINE_DETAIL_QUERY } from '../../../effects/pipeline-editor.effects';
import { SAVE_LABEL_COMMAND } from '../../../effects/label.effects';
import { CASCADE_SAVE_LABEL_COMMAND } from '../../../effects/save-protocol-cascade.effects';
import { saveProtocolCascadeProviders } from '../../../effects/save-protocol-cascade.providers';
import { labelStoreProviders } from '../../../providers/store.providers';
import { PipelineEditorState } from '../../../state/pipeline-editor.state';
import { pipelineStepEditorProviders } from '../pipeline-step-editor/pipeline-step-editor.facade.providers';

import { PipelineEditorFacade } from './pipeline-editor.facade';

const EDITOR_PIPELINE_REPOSITORY = new InjectionToken<PipelineRepository>('EDITOR_PIPELINE_REPOSITORY');
const EDITOR_LABEL_NAMES_PORT = new InjectionToken<ILabelNamesPort>('EDITOR_LABEL_NAMES_PORT');

/**
 * Providers for ONE pipeline editing session (mirror of
 * `resourceEditorSessionProviders`): the editor's own state/effects/facade, the
 * detail read over the session's repository, and the embedded step-editor tree.
 * Spread by every consumer of a session — the route-level page scope composes
 * it into `pipelineEditorProviders` below, and `methodPipelineManagerProviders`
 * spreads it so each manager mount owns a fresh editing session.
 *
 * NOT self-resolvable on its own: the session-tier `PipelineEditorFacade` (and
 * the spread step tier's `PipelineCategoryEditorFacade`) inject the cascade
 * tier's `LabelEffects` / `LabelConsolidationFailureEffects` (and through the
 * step tier, `LabelState` / `LabelConfigState`). Any host mounting this tier
 * without `pipelineEditorCascadeProviders` must make that cascade tier (or an
 * equivalent label scope) reachable through its injector chain, or the facades
 * throw `NullInjectorError`.
 */
export const pipelineEditorSessionProviders: Provider[] = [
  PipelineEditorState,
  {
    provide: EDITOR_PIPELINE_REPOSITORY,
    useFactory: () => new ParsePipelineRepository(Parse, { useMasterKey: false }),
  },
  // The detail read names every category and option the pipeline references, so
  // the drafts it seeds the editor with carry real names instead of the draft
  // projector's uuid placeholders. Its own port instance, like its own
  // repository above: the step tier's is file-private to that tier. Wired
  // without the master key, because the browser has none.
  {
    provide: EDITOR_LABEL_NAMES_PORT,
    useFactory: () => new ParseLabelNamesRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: RETRIEVE_PIPELINE_DETAIL_QUERY,
    useFactory: (pipelineRepo: PipelineRepository, labelNamesPort: ILabelNamesPort) =>
      new RetrievePipelineDetailQuery(pipelineRepo, labelNamesPort),
    deps: [EDITOR_PIPELINE_REPOSITORY, EDITOR_LABEL_NAMES_PORT],
  },
  PipelineEditorEffects,
  PipelineEditorFacade,
  ...pipelineStepEditorProviders,
];

/**
 * Providers for the page/cascade tier: the label staging ledgers
 * (`LabelState` / `LabelConfigState` — staging ledgers exactly like
 * `StagedPipelinesState`, so they live at the page and labels staged inside a
 * pipeline dialog land in the instance the page cascade reads) plus the
 * persist-now cascade and its save commands. Owned by the OUTERMOST consumer
 * only.
 *
 * Seam with the session tier, in both directions:
 * (a) `SaveProtocolCascadeEffects` injects the session tier's
 *     `PipelineEditorState`. Both real consumers (the pipeline editor page via
 *     `pipelineEditorProviders`, the method-type editor page via its own
 *     bundle) flatten the two tiers into one injector, so the row resolves. A
 *     future consumer mounting this cascade tier ABOVE a below-page session
 *     mount needs its own `PipelineEditorState` row here, or the cascade would
 *     reach into one transient session.
 * (b) The session tier's facades inject this tier's `LabelEffects` /
 *     `LabelState` / `LabelConfigState` (see `pipelineEditorSessionProviders`'
 *     doc) — the session tier cannot resolve without this tier or an
 *     equivalent host label scope.
 *
 * Everything the shared cascade class injects beyond the label tier —
 * `LabelConfigState`, the resource + method-type states, and the pipeline /
 * label-config / resource / method-type save commands — comes from the single
 * shared `saveProtocolCascadeProviders` closure. The pipeline page never
 * triggers the resource walk or `consolidateMethodType`, so those rungs are
 * inert here; the method-type page dedupes the same rows with its editor tier in
 * the flattened page injector.
 */
export const pipelineEditorCascadeProviders: Provider[] = [
  ...labelStoreProviders(PaginatedStateLoadingMode.CONTINUOUS),
  {
    provide: CASCADE_SAVE_LABEL_COMMAND,
    useExisting: SAVE_LABEL_COMMAND,
  },
  ...saveProtocolCascadeProviders,
];

/**
 * The flattened page bundle: one editing session + the page/cascade tier in a
 * single injector. `PipelineEditorPageComponent` spreads this unchanged.
 */
export const pipelineEditorProviders: Provider[] = [
  ...pipelineEditorSessionProviders,
  ...pipelineEditorCascadeProviders,
];
